/**
 * Code Intelligence Tabs
 * Tab components for Code DNA, AI Artifacts, Documentation, Tests, and SDLC Ticket processing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { brownfieldApi } from '../../services/api';
import { Dna, Bot, BookOpen, FlaskConical, Ticket, RefreshCw, FileQuestion, AlertTriangle, Coins, Target, Network, Rocket, Server, Wrench, Folder, Package, Clipboard, Plug, Database, Blocks, Sparkles, Palette } from 'lucide-react';
import type { BrownfieldProject } from '../../types';
import type {
  CodeDNA,
  GeneratedAIArtifacts,
  GeneratedDocumentation,
  GeneratedTestSuite,
  SDLCTicketResult,
} from '../../types/code-intelligence.types';
import styles from './BrownfieldPage.module.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Simple progress bar */
const ProgressBar: React.FC<{ percent: number; label: string }> = ({ percent, label }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 4 }}>
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{percent}%</span>
    </div>
    <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-tertiary)', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${percent}%`, borderRadius: 3, background: 'var(--primary-500)', transition: 'width 0.3s ease' }} />
    </div>
  </div>
);

/** Copyable code block */
const CopyBlock: React.FC<{ title: string; content: string; language?: string }> = ({ title, content, language }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{title}</span>
        <button
          onClick={handleCopy}
          style={{
            fontSize: '0.7rem', padding: '3px 10px', borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-primary)', background: copied ? 'var(--color-success)' : 'var(--bg-secondary)',
            color: copied ? '#fff' : 'var(--text-secondary)', cursor: 'pointer',
          }}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <pre style={{
        padding: 16, borderRadius: 'var(--radius-lg)', background: 'var(--bg-secondary)',
        border: '1px solid var(--border-primary)', fontSize: '0.75rem', lineHeight: 1.5,
        fontFamily: 'var(--font-code)', overflow: 'auto', maxHeight: 400,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {content}
      </pre>
    </div>
  );
};

/** SSE progress overlay shared by all generation tabs */
const GenerationOverlay: React.FC<{
  running: boolean;
  progress: { phase: string; percent: number; message: string } | null;
}> = ({ running, progress }) => {
  if (!running) return null;
  return (
    <div style={{
      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-lg)', zIndex: 10,
    }}>
      <div style={{
        background: 'var(--bg-primary)', padding: 32, borderRadius: 'var(--radius-lg)',
        width: 420, boxShadow: 'var(--shadow-lg)', textAlign: 'center',
      }}>
        <div style={{ fontSize: '2rem', marginBottom: 12 }}>🧠</div>
        <h3 style={{ margin: '0 0 8px', fontSize: '1rem' }}>{progress?.phase || 'Starting...'}</h3>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 16px' }}>
          {progress?.message || 'Preparing analysis...'}
        </p>
        <ProgressBar percent={progress?.percent || 0} label={progress?.phase || ''} />
      </div>
    </div>
  );
};

// ─── Code DNA Tab ───────────────────────────────────────────────────────────

export const CodeDNATab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const [codeDNA, setCodeDNA] = useState<CodeDNA | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{ phase: string; percent: number; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load existing Code DNA on mount
  useEffect(() => {
    setLoading(true);
    brownfieldApi.getCodeDNA(project.id)
      .then((res) => { if (res.success && res.data) setCodeDNA(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id]);

  const handleExtract = async () => {
    setGenerating(true);
    setError(null);
    setProgress(null);
    await brownfieldApi.extractCodeDNA(
      project.id,
      (data) => setProgress(data),
      (data) => { setCodeDNA(data); setGenerating(false); },
      (msg) => { setError(msg); setGenerating(false); },
    );
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading Code DNA...</div>;

  const sections: { key: keyof CodeDNA; label: string; icon: React.ReactNode }[] = [
    { key: 'techStack', label: 'Tech Stack', icon: <Wrench size={16} /> },
    { key: 'fileStructure', label: 'File Structure', icon: <Folder size={16} /> },
    { key: 'namingConventions', label: 'Naming Conventions', icon: <FileQuestion size={16} /> },
    { key: 'importPatterns', label: 'Import Patterns', icon: <Package size={16} /> },
    { key: 'errorHandlingPatterns', label: 'Error Handling', icon: <AlertTriangle size={16} /> },
    { key: 'loggingPatterns', label: 'Logging', icon: <Clipboard size={16} /> },
    { key: 'testPatterns', label: 'Testing', icon: <FlaskConical size={16} /> },
    { key: 'apiConventions', label: 'API Patterns', icon: <Plug size={16} /> },
    { key: 'dataAccessPatterns', label: 'Data Access', icon: <Database size={16} /> },
    { key: 'stateManagementPatterns', label: 'State Management', icon: '⚛️' },
    { key: 'documentationStyle', label: 'Documentation Style', icon: '📖' },
    { key: 'codeStyleRules', label: 'Code Style Rules', icon: '✨' },
    { key: 'designIdioms', label: 'Design Idioms', icon: '🎨' },
  ];

  return (
    <div style={{ position: 'relative' }}>
      <GenerationOverlay running={generating} progress={progress} />

      <div className={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 className={styles.sectionTitle} style={{ margin: 0 }}><div style={{ display: "flex", alignItems: "center", gap: "8px" }}><Dna size={20} /> Code DNA</div></h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
              Deep extraction of coding conventions, patterns, and idioms from the codebase.
            </p>
          </div>
          <button className={styles.createButton} onClick={handleExtract} disabled={generating}>
            {codeDNA ? <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><RefreshCw size={16} /> Re-Extract</div> : <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><Dna size={16} /> Extract Code DNA</div>}
          </button>
        </div>

        {error && (
          <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 'var(--radius-md)', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.8rem' }}>
            {error}
          </div>
        )}

        {!codeDNA && !generating && (
          <div style={{
            padding: 48, textAlign: 'center', borderRadius: 'var(--radius-lg)',
            border: '2px dashed var(--border-primary)', color: 'var(--text-secondary)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}><Dna size={48} className={styles.sectionIcon} /></div>
            <p style={{ fontSize: '0.9rem', fontWeight: 500 }}>No Code DNA extracted yet</p>
            <p style={{ fontSize: '0.8rem' }}>
              Extract the coding DNA to understand naming conventions, architectural patterns,
              error handling, testing practices, and more.
            </p>
          </div>
        )}

        {codeDNA && (
          <div className={styles.patternGrid}>
            {sections.map((s) => {
              const data = codeDNA[s.key];
              if (!data) return null;
              return (
                <div key={s.key} className={styles.patternCard}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: '1.2rem' }}>{s.icon}</span>
                    <span className={styles.patternName}>{s.label}</span>
                  </div>
                  <pre style={{
                    fontSize: '0.7rem', fontFamily: 'var(--font-code)', whiteSpace: 'pre-wrap',
                    color: 'var(--text-secondary)', margin: 0, maxHeight: 200, overflow: 'auto',
                  }}>
                    {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
                  </pre>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── AI Artifacts Tab ───────────────────────────────────────────────────────

export const AIArtifactsTab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const [artifacts, setArtifacts] = useState<GeneratedAIArtifacts | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{ phase: string; percent: number; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('instructions');

  useEffect(() => {
    setLoading(true);
    brownfieldApi.getAIArtifacts(project.id)
      .then((res) => { if (res.success && res.data) setArtifacts(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setProgress(null);
    await brownfieldApi.generateAIArtifacts(
      project.id,
      (data) => setProgress(data),
      (data) => { setArtifacts(data); setGenerating(false); },
      (msg) => { setError(msg); setGenerating(false); },
    );
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading AI Artifacts...</div>;

  const sectionTabs = [
    { id: 'instructions', label: 'Instructions' },
    { id: 'agents', label: 'Agents' },
    { id: 'skills', label: '🎯 Skills' },
    { id: 'prompts', label: '💬 Prompts' },
    { id: 'sdlc', label: '🔄 SDLC Config' },
  ];

  return (
    <div style={{ position: 'relative' }}>
      <GenerationOverlay running={generating} progress={progress} />

      <div className={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 className={styles.sectionTitle} style={{ margin: 0 }}><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Bot size={20} /> AI Artifacts</div></h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
              Auto-generated instructions, agents, skills, and prompts tailored to this codebase.
            </p>
          </div>
          <button className={styles.createButton} onClick={handleGenerate} disabled={generating}>
            {artifacts ? <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><RefreshCw size={16} /> Re-Generate</div> : <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Bot size={16} /> Generate AI Artifacts</div>}
          </button>
        </div>

        {error && (
          <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 'var(--radius-md)', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.8rem' }}>
            {error}
          </div>
        )}

        {!artifacts && !generating && (
          <div style={{
            padding: 48, textAlign: 'center', borderRadius: 'var(--radius-lg)',
            border: '2px dashed var(--border-primary)', color: 'var(--text-secondary)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}><Bot size={48} className={styles.sectionIcon} /></div>
            <p style={{ fontSize: '0.9rem', fontWeight: 500 }}>No AI Artifacts generated yet</p>
            <p style={{ fontSize: '0.8rem' }}>
              Generate Copilot instructions, agent definitions, skills, and prompts
              that understand this codebase&apos;s coding style.
            </p>
          </div>
        )}

        {artifacts && (
          <>
            {/* Sub-tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border-primary)', paddingBottom: 4 }}>
              {sectionTabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveSection(t.id)}
                  style={{
                    fontSize: '0.8rem', padding: '6px 14px', borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                    border: 'none', cursor: 'pointer',
                    background: activeSection === t.id ? 'var(--primary-500)' : 'transparent',
                    color: activeSection === t.id ? '#fff' : 'var(--text-secondary)',
                    fontWeight: activeSection === t.id ? 600 : 400,
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Instructions Section */}
            {activeSection === 'instructions' && (
              <div>
                <CopyBlock title="copilot-instructions.md" content={artifacts.copilotInstructions} />
                <CopyBlock title="backend.instructions.md" content={artifacts.backendInstructions} />
                <CopyBlock title="frontend.instructions.md" content={artifacts.frontendInstructions} />
                <CopyBlock title="testing.instructions.md" content={artifacts.testingInstructions} />
                <CopyBlock title="security.instructions.md" content={artifacts.securityInstructions} />
              </div>
            )}

            {/* Agents Section */}
            {activeSection === 'agents' && (
              <div className={styles.patternGrid}>
                {artifacts.agents.map((agent, i) => (
                  <div key={i} className={styles.patternCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: '1.2rem' }}><Bot size={18} /></span>
                      <span className={styles.patternName}>{agent.name}</span>
                    </div>
                    <p className={styles.patternDescription}>{agent.description}</p>
                    <CopyBlock title={`${agent.name}.agent.md`} content={agent.content} />
                  </div>
                ))}
              </div>
            )}

            {/* Skills Section */}
            {activeSection === 'skills' && (
              <div className={styles.patternGrid}>
                {artifacts.skills.map((skill, i) => (
                  <div key={i} className={styles.patternCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: '1.2rem' }}>🎯</span>
                      <span className={styles.patternName}>{skill.name}</span>
                    </div>
                    <p className={styles.patternDescription}>{skill.description}</p>
                    <CopyBlock title={`${skill.name}/SKILL.md`} content={skill.content} />
                  </div>
                ))}
              </div>
            )}

            {/* Prompts Section */}
            {activeSection === 'prompts' && (
              <div className={styles.patternGrid}>
                {artifacts.prompts.map((prompt, i) => (
                  <div key={i} className={styles.patternCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: '1.2rem' }}>💬</span>
                      <span className={styles.patternName}>{prompt.name}</span>
                    </div>
                    <p className={styles.patternDescription}>{prompt.description}</p>
                    <CopyBlock title={`${prompt.name}.prompt.md`} content={prompt.content} />
                  </div>
                ))}
              </div>
            )}

            {/* SDLC Orchestration Section */}
            {activeSection === 'sdlc' && (
              <CopyBlock title="sdlc-orchestration.instructions.md" content={artifacts.sdlcOrchestration} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Generated Documentation Tab ────────────────────────────────────────────

export const GeneratedDocsTab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const [docs, setDocs] = useState<GeneratedDocumentation | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{ phase: string; percent: number; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeDoc, setActiveDoc] = useState<string>('readme');

  useEffect(() => {
    setLoading(true);
    brownfieldApi.getGeneratedDocs(project.id)
      .then((res) => { if (res.success && res.data) setDocs(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setProgress(null);
    await brownfieldApi.generateDocs(
      project.id,
      undefined, // all sections
      (data) => setProgress(data),
      (data) => { setDocs(data); setGenerating(false); },
      (msg) => { setError(msg); setGenerating(false); },
    );
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading documentation...</div>;

  const docSections: { key: keyof GeneratedDocumentation; label: string; icon: React.ReactNode }[] = [
    { key: 'readme', label: 'README', icon: '📄' },
    { key: 'architectureDoc', label: 'Architecture', icon: <Network size={16} /> },
    { key: 'apiDoc', label: 'API Docs', icon: <Plug size={16} /> },
    { key: 'dataModelDoc', label: 'Data Model', icon: <Database size={16} /> },
    { key: 'deploymentDoc', label: 'Deployment', icon: <Rocket size={16} /> },
    { key: 'onboardingGuide', label: 'Onboarding', icon: '👋' },
    { key: 'runbook', label: 'Runbook', icon: <Clipboard size={16} /> },
  ];

  return (
    <div style={{ position: 'relative' }}>
      <GenerationOverlay running={generating} progress={progress} />

      <div className={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 className={styles.sectionTitle} style={{ margin: 0 }}><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><BookOpen size={20} /> Generated Documentation</div></h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
              AI-generated documentation covering architecture, APIs, deployment, and more.
            </p>
          </div>
          <button className={styles.createButton} onClick={handleGenerate} disabled={generating}>
            {docs ? <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><RefreshCw size={16} /> Re-Generate</div> : <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><BookOpen size={16} /> Generate Docs</div>}
          </button>
        </div>

        {error && (
          <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 'var(--radius-md)', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.8rem' }}>
            {error}
          </div>
        )}

        {!docs && !generating && (
          <div style={{
            padding: 48, textAlign: 'center', borderRadius: 'var(--radius-lg)',
            border: '2px dashed var(--border-primary)', color: 'var(--text-secondary)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}><BookOpen size={48} className={styles.sectionIcon} /></div>
            <p style={{ fontSize: '0.9rem', fontWeight: 500 }}>No documentation generated yet</p>
            <p style={{ fontSize: '0.8rem' }}>
              Generate comprehensive documentation including README, architecture guide,
              API docs, deployment guide, and onboarding guide.
            </p>
          </div>
        )}

        {docs && (
          <>
            <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
              {docSections.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setActiveDoc(s.key)}
                  style={{
                    fontSize: '0.78rem', padding: '5px 12px', borderRadius: 'var(--radius-md)',
                    border: activeDoc === s.key ? '1px solid var(--primary-500)' : '1px solid var(--border-primary)',
                    cursor: 'pointer',
                    background: activeDoc === s.key ? 'var(--primary-500)' : 'var(--bg-secondary)',
                    color: activeDoc === s.key ? '#fff' : 'var(--text-secondary)',
                    fontWeight: activeDoc === s.key ? 600 : 400,
                  }}
                >
                  {s.icon} {s.label}
                </button>
              ))}
            </div>

            {docSections.map((s) => (
              activeDoc === s.key && docs[s.key] ? (
                <CopyBlock key={s.key} title={`${s.label}.md`} content={docs[s.key] as string} />
              ) : null
            ))}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Generated Tests Tab ────────────────────────────────────────────────────

export const GeneratedTestsTab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const [tests, setTests] = useState<GeneratedTestSuite | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{ phase: string; percent: number; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('unit');
  const [testTypes, setTestTypes] = useState<('unit' | 'integration' | 'e2e')[]>(['unit', 'integration', 'e2e']);

  useEffect(() => {
    setLoading(true);
    brownfieldApi.getGeneratedTests(project.id)
      .then((res) => { if (res.success && res.data) setTests(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setProgress(null);
    await brownfieldApi.generateTests(
      project.id,
      testTypes,
      undefined,
      (data) => setProgress(data),
      (data) => { setTests(data); setGenerating(false); },
      (msg) => { setError(msg); setGenerating(false); },
    );
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading tests...</div>;

  const testSections = [
    { id: 'unit', label: 'Unit Tests', data: tests?.unitTests },
    { id: 'integration', label: 'Integration Tests', data: tests?.integrationTests },
    { id: 'e2e', label: '🎭 E2E (Playwright)', data: tests?.e2eTests },
    { id: 'pages', label: '📄 Page Objects', data: tests?.pageObjects },
    { id: 'config', label: '⚙️ Config', data: tests?.configFiles },
  ];

  return (
    <div style={{ position: 'relative' }}>
      <GenerationOverlay running={generating} progress={progress} />

      <div className={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 className={styles.sectionTitle} style={{ margin: 0 }}><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FlaskConical size={20} /> Generated Test Suite</div></h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
              Auto-generated unit, integration, and Playwright E2E tests matching the codebase style.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Test type checkboxes */}
            <div style={{ display: 'flex', gap: 8, fontSize: '0.75rem' }}>
              {(['unit', 'integration', 'e2e'] as const).map((t) => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  <input
                    type="checkbox"
                    checked={testTypes.includes(t)}
                    onChange={() => setTestTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t])}
                  />
                  {t}
                </label>
              ))}
            </div>
            <button className={styles.createButton} onClick={handleGenerate} disabled={generating || testTypes.length === 0}>
              {tests ? <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><RefreshCw size={16} /> Re-Generate</div> : <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FlaskConical size={16} /> Generate Tests</div>}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 'var(--radius-md)', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.8rem' }}>
            {error}
          </div>
        )}

        {!tests && !generating && (
          <div style={{
            padding: 48, textAlign: 'center', borderRadius: 'var(--radius-lg)',
            border: '2px dashed var(--border-primary)', color: 'var(--text-secondary)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}><FlaskConical size={48} className={styles.sectionIcon} /></div>
            <p style={{ fontSize: '0.9rem', fontWeight: 500 }}>No tests generated yet</p>
            <p style={{ fontSize: '0.8rem' }}>
              Generate unit tests (Jest), integration tests (supertest), and Playwright E2E
              test specs with page objects — all matching the codebase&apos;s testing conventions.
            </p>
          </div>
        )}

        {tests && (
          <>
            {/* Summary stats */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              {[
                { label: 'Unit', count: tests.unitTests?.length || 0, color: '#6366f1' },
                { label: 'Integration', count: tests.integrationTests?.length || 0, color: '#10b981' },
                { label: 'E2E', count: tests.e2eTests?.length || 0, color: '#f59e0b' },
                { label: 'Page Objects', count: tests.pageObjects?.length || 0, color: '#ec4899' },
              ].map((s) => (
                <div key={s.label} style={{
                  padding: '10px 16px', borderRadius: 'var(--radius-md)',
                  background: s.color + '10', border: `1px solid ${s.color}30`,
                  fontSize: '0.8rem', fontWeight: 500, color: s.color,
                  textAlign: 'center', minWidth: 80,
                }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{s.count}</div>
                  {s.label}
                </div>
              ))}
            </div>

            {/* Section sub-tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
              {testSections.filter((s) => s.data && s.data.length > 0).map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveSection(s.id)}
                  style={{
                    fontSize: '0.78rem', padding: '5px 12px', borderRadius: 'var(--radius-md)',
                    border: activeSection === s.id ? '1px solid var(--primary-500)' : '1px solid var(--border-primary)',
                    cursor: 'pointer',
                    background: activeSection === s.id ? 'var(--primary-500)' : 'var(--bg-secondary)',
                    color: activeSection === s.id ? '#fff' : 'var(--text-secondary)',
                    fontWeight: activeSection === s.id ? 600 : 400,
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Test files */}
            {testSections.map((s) => (
              activeSection === s.id && s.data ? (
                <div key={s.id}>
                  {s.data.map((file: any, i: number) => (
                    <CopyBlock
                      key={i}
                      title={file.filePath || file.path || `test-${i + 1}`}
                      content={file.content || JSON.stringify(file, null, 2)}
                    />
                  ))}
                </div>
              ) : null
            ))}
          </>
        )}
      </div>
    </div>
  );
};

// ─── SDLC Ticket Processing Tab ─────────────────────────────────────────────

export const TicketProcessingTab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const [ticketTitle, setTicketTitle] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketType, setTicketType] = useState<'feature' | 'bugfix' | 'refactor' | 'test' | 'docs'>('feature');
  const [acceptanceCriteria, setAcceptanceCriteria] = useState('');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<{ phase: string; percent: number; message: string } | null>(null);
  const [result, setResult] = useState<SDLCTicketResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activePhase, setActivePhase] = useState<string>('requirementAnalysis');

  const handleProcess = async () => {
    if (!ticketTitle.trim() || !ticketDescription.trim()) return;
    setProcessing(true);
    setError(null);
    setProgress(null);
    setResult(null);

    const criteria = acceptanceCriteria.trim()
      ? acceptanceCriteria.split('\n').filter((l) => l.trim())
      : undefined;

    await brownfieldApi.processTicket(
      project.id,
      { title: ticketTitle.trim(), description: ticketDescription.trim(), type: ticketType, context: criteria?.join('\n') },
      (data) => setProgress(data),
      (data) => { setResult(data); setProcessing(false); },
      (msg) => { setError(msg); setProcessing(false); },
    );
  };

  const phases: { key: keyof SDLCTicketResult; label: string; icon: React.ReactNode }[] = [
    { key: 'requirementAnalysis', label: 'Requirements', icon: <Clipboard size={16} /> },
    { key: 'codeChanges', label: 'Code Changes', icon: '💻' },
    { key: 'codeReview', label: 'Code Review', icon: '🔍' },
    { key: 'testCases', label: 'Test Cases', icon: <FlaskConical size={16} /> },
    { key: 'securityCheck', label: 'Security', icon: '🔒' },
    { key: 'documentationUpdates', label: 'Docs Update', icon: <FileQuestion size={16} /> },
  ];

  return (
    <div style={{ position: 'relative' }}>
      <GenerationOverlay running={processing} progress={progress} />

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Ticket size={24} /> SDLC Ticket Processing</div></h3>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
          Process a development ticket end-to-end. The system uses Code DNA + RAG context to generate
          code changes, reviews, tests, and documentation — all in the codebase&apos;s own style.
        </p>

        {error && (
          <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 'var(--radius-md)', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.8rem' }}>
            {error}
          </div>
        )}

        {!result ? (
          /* Ticket input form */
          <div className={styles.createForm}>
            <div style={{ display: 'flex', gap: 16 }}>
              <div className={styles.formField} style={{ flex: 1 }}>
                <label>Ticket Title *</label>
                <input
                  value={ticketTitle}
                  onChange={(e) => setTicketTitle(e.target.value)}
                  placeholder="e.g. Add user profile avatar upload"
                />
              </div>
              <div className={styles.formField} style={{ width: 160 }}>
                <label>Type</label>
                <select value={ticketType} onChange={(e) => setTicketType(e.target.value as any)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem' }}>
                  <option value="feature">Feature</option>
                  <option value="bugfix">Bugfix</option>
                  <option value="refactor">Refactor</option>
                  <option value="test">Test</option>
                  <option value="docs">Documentation</option>
                </select>
              </div>
            </div>

            <div className={styles.formField}>
              <label>Description *</label>
              <textarea
                value={ticketDescription}
                onChange={(e) => setTicketDescription(e.target.value)}
                placeholder="Describe the feature, bugfix, or task in detail..."
                rows={5}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem', resize: 'vertical', fontFamily: 'var(--font-body)' }}
              />
            </div>

            <div className={styles.formField}>
              <label>Acceptance Criteria (one per line, optional)</label>
              <textarea
                value={acceptanceCriteria}
                onChange={(e) => setAcceptanceCriteria(e.target.value)}
                placeholder="Given a user is logged in&#10;When they upload an avatar&#10;Then the profile picture updates"
                rows={3}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem', resize: 'vertical', fontFamily: 'var(--font-body)' }}
              />
            </div>

            <button
              className={styles.createButton}
              onClick={handleProcess}
              disabled={processing || !ticketTitle.trim() || !ticketDescription.trim()}
              style={{ marginTop: 8 }}
            >
              🚀 Process Ticket
            </button>
          </div>
        ) : (
          /* Results view */
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h4 style={{ margin: 0, fontSize: '1rem' }}>Results: {ticketTitle}</h4>
              <button className={styles.backButton} onClick={() => setResult(null)}>
                ← New Ticket
              </button>
            </div>

            {/* Phase tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
              {phases.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setActivePhase(p.key)}
                  style={{
                    fontSize: '0.78rem', padding: '5px 12px', borderRadius: 'var(--radius-md)',
                    border: activePhase === p.key ? '1px solid var(--primary-500)' : '1px solid var(--border-primary)',
                    cursor: 'pointer',
                    background: activePhase === p.key ? 'var(--primary-500)' : 'var(--bg-secondary)',
                    color: activePhase === p.key ? '#fff' : 'var(--text-secondary)',
                    fontWeight: activePhase === p.key ? 600 : 400,
                  }}
                >
                  {p.icon} {p.label}
                </button>
              ))}
            </div>

            {/* Phase content */}
            {phases.map((p) => {
              if (activePhase !== p.key) return null;
              const data = result[p.key];
              if (!data) return <p key={p.key} style={{ color: 'var(--text-tertiary)' }}>No data for this phase.</p>;
              return (
                <CopyBlock
                  key={p.key}
                  title={`${p.label} — ${ticketTitle}`}
                  content={typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
